import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ── Reusable icon button with tooltip ────────────────────────────────────────
const IconBtn = ({ tooltip, onClick, active, activeClass = "text-blue-normal", children }) => (
    <div className="relative group">
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-150
            hover:scale-105
            ${active ? activeClass : "text-slate-400 hover:text-slate-700 hover:bg-slate-200"}`}
        >
            {children}
        </button>
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-lg
        text-[10px] font-medium text-white bg-slate-800 whitespace-nowrap
        opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
            {tooltip}
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </span>
    </div>
);

const getStageText = (stage, t) => (stage && stage.replace(/_/g, ' ')) || (stage ? "Processing..." : t('aura message thinking'));

const MessageList = ({
    viewDensity,
    isLoadingHistory,
    groupedMessages,
    isSending,
    thinkingStage,
    copyNoticeId,
    messagesEndRef,
    onCopy,
    setCopyNoticeId,
    onUpdateMessage,
    onStartEdit,
    onReact,
    formatTimestamp,
}) => {
    const { t } = useTranslation();
    const thinkingText = getStageText(thinkingStage, t);
    const hasStreamingMessage = groupedMessages.some(
        (item) => item.type === "message" && item.message?.streaming
    );
    return (
        <div
            className={`flex-1 overflow-y-auto px-6 py-6 space-y-5 min-h-0 ${
                viewDensity === "compact" ? "text-sm" : "text-base"
            }`}
        >
            {isLoadingHistory && (
                <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, idx) => (
                        <div
                            key={`h-${idx}`}
                            className="h-20 rounded-[22px] bg-white/35 border border-white/40 animate-pulse"
                        />
                    ))}
                </div>
            )}

            {!isLoadingHistory &&
                groupedMessages.map((item, index) => {
                    if (item.type === "separator") {
                        return (
                            <div
                                key={`sep-${index}`}
                                className="text-center text-[11px] text-slate-500"
                            >
                                {item.label}
                            </div>
                        );
                    }

                    const message = item.message;

                    return (
                        <div
                            key={message.id}
                            className={`flex ${
                                message.role === "user"
                                    ? "justify-end"
                                    : "justify-center"
                            }`}
                        >
                            <div
                                className={`${
                                    message.role === "assistant"
                                        ? "w-full max-w-none"
                                        : "max-w-[78%]"
                                }`}
                            >
                                <div
                                    className={`px-3 ${
                                        viewDensity === "compact"
                                            ? "py-2"
                                            : "py-3"
                                    } text-sm leading-relaxed rounded-[18px]
                                    ${
                                        message.role === "user"
                                            ? "text-white border-white/10 bg-gradient-to-r from-indigo-600 to-violet-700 shadow-[0_22px_60px_rgba(79,70,229,0.32)]"
                                            : "text-slate-900 "
                                    }`}
                                >
                                    {message.role === "assistant" ? (
                                        <div className="ai-markdown">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    h1: ({ node, ...props }) => (
                                                        <h1 className="text-lg font-bold text-slate-900 mt-4 mb-2" {...props} />
                                                    ),
                                                    h2: ({ node, ...props }) => (
                                                        <h2 className="text-base font-bold text-slate-800 mt-4 mb-2" {...props} />
                                                    ),
                                                    h3: ({ node, ...props }) => (
                                                        <h3 className="text-sm font-semibold text-slate-800 mt-3 mb-1.5" {...props} />
                                                    ),
                                                    p: ({ node, ...props }) => (
                                                        <p className="text-sm text-slate-700 leading-relaxed mb-2" {...props} />
                                                    ),
                                                    ul: ({ node, ...props }) => (
                                                        <ul className="list-disc list-outside pl-5 mb-2 space-y-1 text-sm text-slate-700" {...props} />
                                                    ),
                                                    ol: ({ node, ...props }) => (
                                                        <ol className="list-decimal list-outside pl-5 mb-2 space-y-1 text-sm text-slate-700" {...props} />
                                                    ),
                                                    li: ({ node, ...props }) => (
                                                        <li className="leading-relaxed" {...props} />
                                                    ),
                                                    strong: ({ node, ...props }) => (
                                                        <strong className="font-semibold text-slate-900" {...props} />
                                                    ),
                                                    em: ({ node, ...props }) => (
                                                        <em className="italic text-slate-600" {...props} />
                                                    ),
                                                    hr: ({ node, ...props }) => (
                                                        <hr className="my-3 border-slate-200" {...props} />
                                                    ),
                                                    blockquote: ({ node, ...props }) => (
                                                        <blockquote className="border-l-2 border-indigo-300 pl-3 my-2 text-slate-500 italic text-sm" {...props} />
                                                    ),
                                                    code: ({ node, inline, ...props }) =>
                                                        inline ? (
                                                            <code className="bg-slate-100 text-indigo-700 rounded px-1 py-0.5 text-xs font-mono" {...props} />
                                                        ) : (
                                                            <code className="block bg-slate-100 text-slate-800 rounded-lg p-3 text-xs font-mono overflow-x-auto" {...props} />
                                                        ),
                                                    pre: ({ node, ...props }) => (
                                                        <pre className="my-2 rounded-lg overflow-x-auto" {...props} />
                                                    ),
                                                    table: ({ node, ...props }) => (
                                                        <div className="border backdrop-blur-xl rounded-[14px] p-2 overflow-x-auto my-2">
                                                            <table className="w-full text-xs text-left" {...props} />
                                                        </div>
                                                    ),
                                                    thead: ({ node, ...props }) => (
                                                        <thead className="bg-slate-100 text-slate-600 font-semibold" {...props} />
                                                    ),
                                                    th: ({ node, ...props }) => (
                                                        <th className="px-3 py-2 border-b border-slate-200" {...props} />
                                                    ),
                                                    td: ({ node, ...props }) => (
                                                        <td className="px-3 py-2 border-b border-slate-100" {...props} />
                                                    ),
                                                }}
                                            >
                                                {message.content}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        message.content
                                    )}

                                    <div className="flex items-center justify-between">
                                        {message.role === "assistant" && (
                                            <div className="mt-3 flex items-center gap-0.5">
                                                <IconBtn
                                                    tooltip={copyNoticeId === message.id ? t('aura message copied') : t('aura message copy')}
                                                    onClick={() => {
                                                        onCopy(message.content);
                                                        setCopyNoticeId(message.id);
                                                        setTimeout(() => setCopyNoticeId((p) => p === message.id ? "" : p), 1500);
                                                    }}
                                                >
                                                    {copyNoticeId === message.id ? (
                                                        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                                                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                                                            <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
                                                            <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                                        </svg>
                                                    )}
                                                </IconBtn>

                                                <IconBtn
                                                    tooltip={t('aura message helpful')}
                                                    active={message.reaction === "up"}
                                                    activeClass="text-emerald-500 bg-emerald-50"
                                                    onClick={() => onReact(message, "up")}
                                                >
                                                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                                                        <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" fill={message.reaction === "up" ? "currentColor" : "none"} />
                                                        <path d="M7 11l4-7a2 2 0 0 1 2-1h1a2 2 0 0 1 2 2v3h3a2 2 0 0 1 2 2l-2 7a2 2 0 0 1-2 2H7" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" fill={message.reaction === "up" ? "currentColor" : "none"} />
                                                    </svg>
                                                </IconBtn>

                                                <IconBtn
                                                    tooltip={t('aura message not helpful')}
                                                    active={message.reaction === "down"}
                                                    activeClass="text-rose-500 bg-rose-50"
                                                    onClick={() => onReact(message, "down")}
                                                >
                                                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                                                        <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" fill={message.reaction === "down" ? "currentColor" : "none"} />
                                                        <path d="M17 13l-4 7a2 2 0 0 1-2 1h-1a2 2 0 0 1-2-2v-3H5a2 2 0 0 1-2-2l2-7a2 2 0 0 1 2-2h10" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" fill={message.reaction === "down" ? "currentColor" : "none"} />
                                                    </svg>
                                                </IconBtn>
                                            </div>
                                        )}
                                        <div
                                            className={`mt-1 text-[10px] opacity-70 ${
                                                message.role === "user"
                                                    ? "text-white/80 text-right"
                                                    : "text-slate-500 text-right"
                                            }`}
                                        >
                                            {formatTimestamp(message.createdAt)}
                                        </div>
                                    </div>
                                </div>
                                {/* Meta actions (assistant only) */}

                                {message.role === "user" && (
                                    <div className="mt-2 flex items-center gap-0.5 justify-end">
                                        <IconBtn
                                            tooltip={copyNoticeId === message.id ? t('aura message copied') : t('aura message copy user')}
                                            activeClass="text-indigo-600"
                                            onClick={() => {
                                                onCopy(message.content);
                                                setCopyNoticeId(message.id);
                                                setTimeout(() => setCopyNoticeId((p) => p === message.id ? "" : p), 1500);
                                            }}
                                        >
                                            {copyNoticeId === message.id ? (
                                                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                                                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            ) : (
                                                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                                                    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
                                                    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                                </svg>
                                            )}
                                        </IconBtn>

                                        <IconBtn tooltip={t('aura message edit')} activeClass="text-indigo-600" onClick={() => onStartEdit(message)}>
                                            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </IconBtn>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

            {isSending && !hasStreamingMessage && (
                <div className="flex justify-start">
                    <div className="text-[11px] text-blue-normal flex items-center gap-2">
                        <img
                            src="/assets/images/aura-icon.png"
                            alt="Aura AI"
                            className="h-5 w-5"
                        />
                        <span className="transition-all duration-300">{thinkingText}</span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-normal animate-bounce" />
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-normal animate-bounce [animation-delay:120ms]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-normal animate-bounce [animation-delay:240ms]" />
                        </span>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default MessageList;
